/**
 * API缓存管理器
 *
 * 功能特性：
 * 1. 基于时间的缓存策略（默认5分钟TTL）
 * 2. GET请求自动缓存
 * 3. 支持缓存失效和清理机制
 * 4. 内存使用限制和LRU淘汰策略
 * 5. 请求去重功能
 */

// 缓存项接口
interface CacheItem<T = any> {
  data: T;
  timestamp: number;
  ttl: number; // 生存时间（毫秒）
  size: number; // 估算的数据大小（字节）
}

// 缓存配置接口
interface CacheConfig {
  defaultTTL: number; // 默认TTL（毫秒）
  maxSize: number; // 最大缓存大小（MB）
  maxItems: number; // 最大缓存项数量
  cleanupInterval: number; // 清理间隔（毫秒）
}

// 请求去重映射
interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

/**
 * API缓存管理器类
 */
export class ApiCacheManager {
  private cache = new Map<string, CacheItem>();
  private pendingRequests = new Map<string, PendingRequest>();
  private accessOrder = new Map<string, number>(); // LRU访问顺序
  private accessCounter = 0;

  private config: CacheConfig = {
    defaultTTL: 5 * 60 * 1000, // 5分钟
    maxSize: 50, // 50MB
    maxItems: 1000, // 最大1000个缓存项
    cleanupInterval: 2 * 60 * 1000, // 2分钟清理一次
  };

  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor(config?: Partial<CacheConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.startCleanup();
  }

  /**
   * 生成缓存键
   */
  private generateCacheKey(url: string, params?: any): string {
    const baseKey = url;
    if (!params) return baseKey;

    // 对参数进行排序以确保一致性
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {} as any);

    return `${baseKey}?${JSON.stringify(sortedParams)}`;
  }

  /**
   * 估算数据大小（字节）
   */
  private estimateSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      // 如果JSON.stringify失败，使用简单估算
      return JSON.stringify(data || {}).length * 2; // UTF-16编码，每字符2字节
    }
  }

  /**
   * 更新访问顺序（LRU）
   */
  private updateAccessOrder(key: string): void {
    this.accessOrder.set(key, ++this.accessCounter);
  }

  /**
   * 检查缓存项是否过期
   */
  private isExpired(item: CacheItem): boolean {
    return Date.now() - item.timestamp > item.ttl;
  }

  /**
   * 获取缓存
   */
  get<T = any>(url: string, params?: any): T | null {
    const key = this.generateCacheKey(url, params);
    const item = this.cache.get(key);

    if (!item || this.isExpired(item)) {
      if (item) {
        this.cache.delete(key);
        this.accessOrder.delete(key);
      }
      return null;
    }

    // 更新访问顺序
    this.updateAccessOrder(key);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[API缓存] 命中缓存: ${key}`);
    }

    return item.data;
  }

  /**
   * 设置缓存
   */
  set<T = any>(url: string, data: T, params?: any, ttl?: number): void {
    const key = this.generateCacheKey(url, params);
    const size = this.estimateSize(data);
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
      size,
    };

    // 检查是否需要清理空间
    this.ensureSpace(size);

    this.cache.set(key, item);
    this.updateAccessOrder(key);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[API缓存] 设置缓存: ${key}, 大小: ${(size / 1024).toFixed(2)}KB`);
    }
  }

  /**
   * 确保有足够的缓存空间
   */
  private ensureSpace(newItemSize: number): void {
    const maxSizeBytes = this.config.maxSize * 1024 * 1024;
    let currentSize = this.getCurrentSize();

    // 如果超过最大项数或大小限制，使用LRU策略清理
    while (
      (this.cache.size >= this.config.maxItems || currentSize + newItemSize > maxSizeBytes) &&
      this.cache.size > 0
    ) {
      const oldestKey = this.findLRUKey();
      if (oldestKey) {
        const item = this.cache.get(oldestKey);
        if (item) {
          currentSize -= item.size;
        }
        this.cache.delete(oldestKey);
        this.accessOrder.delete(oldestKey);
      } else {
        break;
      }
    }
  }

  /**
   * 找到最近最少使用的键
   */
  private findLRUKey(): string | null {
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;

    for (const [key, accessTime] of this.accessOrder.entries()) {
      if (accessTime < oldestAccess) {
        oldestAccess = accessTime;
        oldestKey = key;
      }
    }

    return oldestKey;
  }

  /**
   * 获取当前缓存总大小
   */
  private getCurrentSize(): number {
    let totalSize = 0;
    for (const item of this.cache.values()) {
      totalSize += item.size;
    }
    return totalSize;
  }

  /**
   * 删除特定缓存
   */
  delete(url: string, params?: any): boolean {
    const key = this.generateCacheKey(url, params);
    const deleted = this.cache.delete(key);
    if (deleted) {
      this.accessOrder.delete(key);
    }
    return deleted;
  }

  /**
   * 按前缀删除缓存
   */
  deleteByPrefix(prefix: string): number {
    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.accessOrder.delete(key);
    });
    return keysToDelete.length;
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder.clear();
    this.pendingRequests.clear();
    this.accessCounter = 0;

    if (process.env.NODE_ENV === 'development') {
      console.log('[API缓存] 清空所有缓存');
    }
  }

  /**
   * 清理过期缓存
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (this.isExpired(item)) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => {
      this.cache.delete(key);
      this.accessOrder.delete(key);
    });

    // 清理过期的待处理请求
    const expiredRequestKeys: string[] = [];
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > 30000) {
        // 30秒超时
        expiredRequestKeys.push(key);
      }
    }

    expiredRequestKeys.forEach(key => {
      this.pendingRequests.delete(key);
    });

    if (process.env.NODE_ENV === 'development' && expiredKeys.length > 0) {
      console.log(`[API缓存] 清理了 ${expiredKeys.length} 个过期缓存项`);
    }
  }

  /**
   * 开始定期清理
   */
  private startCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * 停止清理定时器
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clear();
  }

  /**
   * 请求去重：如果相同请求正在进行中，返回同一个Promise
   */
  deduplicateRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    const existing = this.pendingRequests.get(key);

    if (existing) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API缓存] 请求去重: ${key}`);
      }
      return existing.promise;
    }

    const promise = requestFn().finally(() => {
      // 请求完成后清理
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * 获取缓存统计信息
   */
  getStats(): {
    itemCount: number;
    totalSize: number; // MB
    hitRate: number;
    maxSize: number; // MB
    maxItems: number;
  } {
    const totalSize = this.getCurrentSize();

    return {
      itemCount: this.cache.size,
      totalSize: Math.round((totalSize / 1024 / 1024) * 100) / 100,
      hitRate: 0, // TODO: 实现命中率统计
      maxSize: this.config.maxSize,
      maxItems: this.config.maxItems,
    };
  }
}

// 创建全局缓存实例
export const apiCache = new ApiCacheManager();

// 导出默认实例
export default apiCache;
